import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  ensureAnonymousSignIn,
  auth,
  getIdToken,
} from '@/lib/firebase'
import { useAuthStore } from '@/stores/auth'
import { GET_TEAM_ADMIN_INVITE_INFO } from '@/graphql/queries/team'
import { REDEEM_TEAM_ADMIN_INVITE } from '@/graphql/mutations/teams'
import { cx } from '@/lib/utils'
import { trackEvent } from '@/hooks/useMixpanel'

const NOT_ADMIN_ERROR_MESSAGE =
  'You are not an admin of any account. Please reach out to support@rollavideo.com if you think this was a mistake.'

function inviteInfoMessage(reason: string | null | undefined): string {
  if (reason === 'expired') return 'This invite link has expired. Ask the team admin for a new one.'
  if (reason === 'revoked') return 'This invite link is no longer valid.'
  return "This invite link isn't valid."
}

// Pre-existing race, surfaced while testing the invite flow: navigating to /campaigns right
// after a real sign-in resolves can beat Firebase's onAuthStateChanged listener to updating the
// auth store, so ProtectedRoute reads a still-stale isAuthenticated: false and silently bounces
// back to /login — no error, since it's a routing decision, not a failure. Waiting for the store
// to actually reflect the real user first (with a timeout as a safety net) closes that window.
function waitForAuthenticated(timeoutMs = 3000): Promise<void> {
  if (useAuthStore.getState().isAuthenticated) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe()
      resolve()
    }, timeoutMs)
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.isAuthenticated) {
        clearTimeout(timer)
        unsubscribe()
        resolve()
      }
    })
  })
}

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const notAdminError = useAuthStore((s) => s.notAdminError)
  const setNotAdminError = useAuthStore((s) => s.setNotAdminError)
  const setRedeemingInvite = useAuthStore((s) => s.setRedeemingInvite)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Only ever true once an invite code in the URL has been confirmed valid by the backend —
  // the create-account form and sign-up-disabled bypass are both gated on this, not on the raw
  // presence of ?invite= in the URL.
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')

  const inviteCode = searchParams.get('invite')
  // The invite-info query below needs a real token on the wire, not just an eventually-resolved
  // one — Apollo's authLink reads auth.currentUser synchronously per request, so firing the
  // query before ensureAnonymousSignIn() actually resolves sends it with no Authorization header
  // at all (the backend hard-rejects that outright, independent of graphql-shield rules).
  const [anonymousReady, setAnonymousReady] = useState(false)

  // Gives the page a working auth token before any real sign-in — needed so backend calls
  // made from this screen (the invite-info lookup below) have something to authenticate with,
  // since the API requires a valid token on every query.
  useEffect(() => {
    ensureAnonymousSignIn()
      .then(() => setAnonymousReady(true))
      .catch(() => {})
  }, [])

  const { data: inviteInfoData } = useQuery(GET_TEAM_ADMIN_INVITE_INFO, {
    variables: { code: inviteCode ?? '' },
    skip: !inviteCode || !anonymousReady,
  })
  const inviteInfo = inviteInfoData?.getTeamAdminInviteInfo
  const validInvite = inviteInfo?.valid ? inviteCode : null

  // Land straight in Create Account mode once we know the invite is real — the whole point of
  // this link is inviting someone new, not asking them to notice a toggle. They can still switch
  // back to Sign In manually if it turns out they already have an account.
  useEffect(() => {
    if (validInvite) setMode('signup')
  }, [validInvite])

  const [redeemInvite] = useMutation(REDEEM_TEAM_ADMIN_INVITE, {
    refetchQueries: ['GetTeamAdminsByProfileId'],
    awaitRefetchQueries: true,
  })

  // Forces a fresh ID token before redeeming. Right after sign-up/sign-in, auth.currentUser is
  // already the new real user, but the token Apollo's authLink would otherwise reuse can be a
  // moment stale (or, for a just-created account, still propagating on Firebase's backend) —
  // narrow enough to pass most of the time and fail occasionally, which is exactly what makes it
  // dangerous to leave unguarded on a flow that grants admin access.
  async function redeemWithFreshToken(code: string) {
    if (auth.currentUser) await getIdToken(auth.currentUser, true)
    await redeemInvite({ variables: { code } })
  }

  async function afterAuthSuccess(method: string) {
    trackEvent('Log In', { method })
    // Let the rest of the app (useBootstrap, ProtectedRoute) actually recognize the real user
    // before doing anything that depends on it — both the invite redemption's refetch and the
    // navigate below need firebaseUser/isAuthenticated to be current, not the anonymous session's
    // stale snapshot.
    await waitForAuthenticated()
    if (validInvite) {
      setRedeemingInvite(true)
      try {
        await redeemWithFreshToken(validInvite)
      } catch {
        try {
          // One retry after a short delay — covers the token-propagation case above, which a
          // second, later attempt with another fresh token resolves on its own.
          await new Promise((resolve) => setTimeout(resolve, 800))
          await redeemWithFreshToken(validInvite)
        } catch {
          // Swallow — if this genuinely failed (e.g. the link was revoked in the moment between
          // page load and submit), useBootstrap's own "not an admin of any team" kickout takes
          // over as soon as redeemingInvite flips back false below and reflects reality.
        }
      } finally {
        setRedeemingInvite(false)
      }
    }
    navigate('/campaigns')
  }

  function getErrorMessage(code: string): string {
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect password. Please try again.'
      case 'auth/user-not-found':
        return 'No account found with this email.'
      case 'auth/popup-blocked':
        return 'Popup was blocked. Please allow popups and try again.'
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed. Please try again.'
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.'
      case 'auth/sign-up-disabled':
        return "New accounts aren't available yet. Please contact support@rollavideo.com."
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method. Please sign in with your email and password instead.'
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please sign in instead.'
      case 'auth/weak-password':
        return 'Password must be at least 6 characters.'
      case 'auth/invalid-email':
        return 'Please enter a valid email address.'
      default:
        return 'An unexpected error occurred. Please try again.'
    }
  }

  async function handleGoogleSignIn() {
    setError(null)
    setNotAdminError(false)
    setLoading(true)
    try {
      await signInWithGoogle(!!validInvite)
      await afterAuthSuccess('google')
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? ''
      setError(getErrorMessage(code))
    } finally {
      setLoading(false)
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setNotAdminError(false)

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      if (mode === 'signup') {
        await signUpWithEmail(email, password)
        await afterAuthSuccess('email_signup')
      } else {
        await signInWithEmail(email, password)
        await afterAuthSuccess('email')
      }
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? ''
      setError(getErrorMessage(code))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gray-50 px-4 py-10">
      {/* Radiating glow */}
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-40 blur-[120px]"
        style={{ backgroundColor: '#e74f31' }}
        aria-hidden="true"
      />

      {/* Auth box */}
      <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-xl sm:p-10">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/rolla-logo.png" alt="Rolla" className="h-7 w-auto" />
          <p className="mt-2 text-sm text-gray-500">Admin Portal</p>
        </div>

        {inviteCode &&
          (validInvite ? (
            <div className="mb-6 flex flex-col items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-center">
              {inviteInfo?.teamLogoURL && (
                <img src={inviteInfo.teamLogoURL} alt="" className="h-8 w-8 rounded object-cover" />
              )}
              <p className="text-sm text-primary-700">
                You&apos;re invited to be an admin of <span className="font-semibold">{inviteInfo?.teamName}</span>.
              </p>
            </div>
          ) : (
            inviteInfo && (
              <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm text-gray-600">
                {inviteInfoMessage(inviteInfo.reason)}
              </div>
            )
          ))}

        <h2 className="mb-6 text-center text-xl font-semibold text-gray-900">
          {mode === 'signup' ? 'Create your account' : 'Sign in to your account'}
        </h2>

        {notAdminError && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {NOT_ADMIN_ERROR_MESSAGE}
          </div>
        )}

        {/* Google SSO */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:shadow disabled:opacity-50"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs text-gray-400">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Email/Password form */}
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
              placeholder="Enter your password"
            />
          </div>

          {mode === 'signup' && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                placeholder="Re-enter your password"
              />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={cx(
              'w-full rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors',
              'bg-primary-600 hover:bg-primary-700 disabled:opacity-50',
            )}
          >
            {loading ? 'Please wait...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        {/* This toggle only ever renders once a valid admin-invite code (ADM-572/BAC-1739) has
            been confirmed for this page load — it switches between redeeming that invite
            (joining an existing team) and signing in, not general account creation. */}
        {validInvite && (
          <button
            type="button"
            onClick={() => {
              setError(null)
              setMode((m) => (m === 'signup' ? 'signin' : 'signup'))
            }}
            className="mt-4 w-full text-center text-sm text-primary-600 hover:text-primary-700"
          >
            {mode === 'signup' ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
          </button>
        )}

        {/* Front-door signup link for everyone else (no invite code) — the only path to /signup
            from this page; previously missing entirely, so a visitor with no account and no
            invite had no way to discover self-service signup existed. */}
        {!validInvite && mode === 'signin' && (
          <p className="mt-4 text-center text-sm text-gray-500">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary-600 hover:text-primary-700">
              Sign up
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}
